import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  ViewModule,
  Description,
  VerifiedUser,
  Logout,
  Person,
  ChevronLeft,
  AccountTree,
  Hub,
  Business,
  Settings,
  AdminPanelSettings,
  FolderOpen,
  ShowChart,
  HelpOutline,
  ManageSearch,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useIsViewer, useIsDemoSession } from '../hooks/useDemoViewer';
import DemoWatermark from './DemoWatermark';
import ImpersonationBanner from './ImpersonationBanner';
import ApplianceLicenceBanner from './ApplianceLicenceBanner';
import { useDevToolsDetection } from '../hooks/useDevToolsDetection';
import IrisNotificationCenter from './IrisNotificationCenter';

const drawerWidth = 280;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const user = authAPI.getCurrentUser();
  const isImpersonating =
    typeof window !== 'undefined' &&
    !!localStorage.getItem('prism_impersonation_original_token');
  const isViewer = useIsViewer();
  // The demo lockdown keys on the demo SESSION, never the Viewer role (F11).
  const isDemo = useIsDemoSession();
  const devToolsOpen = useDevToolsDetection(isDemo);

  // Demo viewer: block printing and text selection
  React.useEffect(() => {
    if (!isDemo) return;
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * { display: none !important; }
        body::after {
          content: 'Printing is disabled in demo mode.';
          display: block !important;
          font-size: 24px;
          text-align: center;
          padding: 100px;
          color: #666;
        }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, [isDemo]);

  // Dynamic page title for screen readers and browser tab
  useEffect(() => {
    const path = location.pathname;
    let title = 'PRISM Risk Aperture';
    if (path === '/dashboard') title = 'Security Dashboard — PRISM Risk Aperture';
    else if (path.startsWith('/projects-v2')) title = 'Projects — PRISM Risk Aperture';
    else if (path.includes('/compliance/')) title = 'Compliance Assessment — PRISM Risk Aperture';
    else if (path.includes('/modeling')) title = 'System Modeling — PRISM Risk Aperture';
    else if (path.includes('/risk')) title = 'Portfolio Status — PRISM Risk Aperture';
    else if (path.includes('/threat-intelligence')) title = 'Threat Intelligence — PRISM Risk Aperture';
    else if (path.includes('/cmmc')) title = 'CMMC Assessment — PRISM Risk Aperture';
    else if (path.includes('/zero-trust')) title = 'Zero Trust Assessment — PRISM Risk Aperture';
    else if (path === '/trust-center') title = 'Trust Center — PRISM Risk Aperture';
    else if (path === '/reports') title = 'Security Reports — PRISM Risk Aperture';
    else if (path === '/settings') title = 'Settings — PRISM Risk Aperture';
    else if (path === '/admin') title = 'Admin Console — PRISM Risk Aperture';
    else if (path === '/admin/iris/quality') title = 'Iris Quality — PRISM Risk Aperture';
    else if (path === '/admin/help/trends') title = 'Help Searches — PRISM Risk Aperture';
    else if (path === '/help') title = 'Help — PRISM Risk Aperture';
    else if (path === '/integrations') title = 'Integrations — PRISM Risk Aperture';
    else if (path === '/corporate-dashboard') title = 'Corporate Rollup — PRISM Risk Aperture';
    else if (path.includes('/evidence')) title = 'Evidence Library — PRISM Risk Aperture';
    else if (path.includes('/ai-governance')) title = 'AI Governance — PRISM Risk Aperture';
    else if (path.includes('/packages')) title = 'Compliance Packages — PRISM Risk Aperture';
    else if (path.includes('/cost-optimization')) title = 'Cost Optimization — PRISM Risk Aperture';
    else if (path.includes('/budget')) title = 'Budget & Investment — PRISM Risk Aperture';
    else if (path.includes('/foundations')) title = 'Foundations Integration — PRISM Risk Aperture';
    document.title = title;
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerOpenToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  // Nav items hidden from demo viewers (config/admin pages)
  const viewerHiddenPaths = ['/settings', '/integrations', '/trust-center'];

  const allMenuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      color: '#00d9ff',
    },
    {
      text: 'Projects',
      icon: <AccountTree />,
      path: '/projects-v2',
      color: '#64ffda',
    },
    {
      text: 'Portfolio Status',
      icon: <ViewModule />,
      path: '/risk',
      color: '#ffa500',
    },
    {
      text: 'Corporate Rollup',
      icon: <Business />,
      path: '/corporate-dashboard',
      color: '#a78bfa',
    },
    {
      text: 'Trust Center',
      icon: <VerifiedUser />,
      path: '/trust-center',
      color: '#00d9ff',
    },
    {
      text: 'Integrations',
      icon: <Hub />,
      path: '/integrations',
      color: '#f472b6',
    },
    {
      text: 'Documents',
      icon: <FolderOpen />,
      path: '/documents',
      color: '#60a5fa',
    },
    {
      text: 'Reports',
      icon: <Description />,
      path: '/reports',
      color: '#a78bfa',
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/settings',
      color: '#a8b2cc',
    },
    // PRISM help guide (spec 2026-10-25-prism-help-guide-design): every signed-in role, viewers included.
    {
      text: 'Help',
      icon: <HelpOutline />,
      path: '/help',
      color: '#a8b2cc',
    },
  ];

  // Admin Console — only rendered for Risk Aperture superadmins (Pat, Chuck, Roger).
  // Hidden from every other user so customers never see an "Admin" button.
  const adminMenuItem = {
    text: 'Admin Console',
    icon: <AdminPanelSettings />,
    path: '/admin',
    color: '#ffa500',
  };

  // Iris Quality dashboard — Phase A AI Eval Harness drift visibility.
  // Same superadmin gate as Admin Console.
  const irisQualityMenuItem = {
    text: 'Iris Quality',
    icon: <ShowChart />,
    path: '/admin/iris/quality',
    color: '#ffa500',
  };

  // Help Searches — what people type into /help, since last Thursday. Same superadmin gate as Admin Console.
  const helpTrendsMenuItem = {
    text: 'Help Searches',
    icon: <ManageSearch />,
    path: '/admin/help/trends',
    color: '#ffa500',
  };

  const baseMenuItems = isViewer
    ? allMenuItems.filter((item) => !viewerHiddenPaths.includes(item.path))
    : allMenuItems;
  const menuItems = user?.is_superadmin
    ? [...baseMenuItems, adminMenuItem, irisQualityMenuItem, helpTrendsMenuItem]
    : baseMenuItems;

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #0a192f 0%, #0d1f3c 50%, #112240 100%)',
    }}>
      {/* C7 Feedback 3 #6 — collapse-aware header. At the 64px collapsed width
          the full-width wordmark + p:3 padding overflowed and got clipped by
          overflowX:hidden, which read as a broken render. Reduce padding and
          contain-scale the logo so it fits the rail cleanly in both states. */}
      <Box
        sx={{
          p: drawerOpen ? 3 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src="/risk-aperture-logo.svg"
          alt="Risk Aperture"
          style={{
            height: drawerOpen ? 48 : 'auto',
            width: drawerOpen ? 'auto' : '100%',
            maxWidth: '100%',
            objectFit: 'contain',
            filter: 'brightness(0) invert(1)',
          }}
        />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <List sx={{ flexGrow: 1, px: { xs: 2, sm: drawerOpen ? 2 : 0.5 }, py: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname.startsWith(item.path)}
              sx={{
                borderRadius: 2,
                py: 1.2,
                justifyContent: { xs: 'initial', sm: drawerOpen ? 'initial' : 'center' },
                px: { xs: 2, sm: drawerOpen ? 2 : 1 },
                '&.Mui-selected': {
                  bgcolor: 'rgba(0, 217, 255, 0.12)',
                  borderLeft: '3px solid #00d9ff',
                  color: '#e6f1ff',
                  '& .MuiListItemIcon-root': {
                    color: '#00d9ff',
                  },
                  '&:hover': {
                    bgcolor: 'rgba(0, 217, 255, 0.18)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: item.color,
                  minWidth: { xs: 40, sm: drawerOpen ? 40 : 0 },
                  mr: { sm: drawerOpen ? 0 : 'auto' },
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{ display: { xs: 'block', sm: drawerOpen ? 'block' : 'none' } }}
                primaryTypographyProps={{
                  sx: { color: '#ccd6f6', fontSize: '0.9rem', fontWeight: 500 },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Collapse-aware footer — full wordmark when open, just "©" centered
          when collapsed so it doesn't wrap/clip in the 64px rail (C7 #6). */}
      <Box component="footer" sx={{ p: 2, textAlign: drawerOpen ? 'left' : 'center' }}>
        <Typography variant="caption" sx={{ color: '#7a8aaa', whiteSpace: 'nowrap' }}>
          {drawerOpen ? '© 2026 Risk Aperture' : '©'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', pt: isImpersonating ? '44px' : 0 }}>
      <ImpersonationBanner />
      {/* Skip navigation link — visible only on focus */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 1500,
          padding: '8px 16px',
          background: '#00d9ff',
          color: '#0a192f',
          fontWeight: 600,
          fontSize: '0.875rem',
          textDecoration: 'none',
          borderRadius: '0 0 8px 0',
        }}
        onFocus={(e) => { e.currentTarget.style.left = '0'; e.currentTarget.style.top = '0'; }}
        onBlur={(e) => { e.currentTarget.style.left = '-9999px'; }}
      >
        Skip to main content
      </a>

      <AppBar
        component="header"
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerOpen ? drawerWidth : 64}px)` },
          ml: { sm: `${drawerOpen ? drawerWidth : 64}px` },
          transition: 'all 0.3s',
          bgcolor: 'rgba(10, 25, 47, 0.8)',
          backdropFilter: 'blur(12px)',
          color: '#e6f1ff',
          boxShadow: '0 1px 0 rgba(255,255,255,0.06)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label={drawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            edge="start"
            onClick={handleDrawerOpenToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>

          <IconButton
            color="inherit"
            aria-label="Open navigation menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {(() => {
              const p = location.pathname;
              if (p === '/dashboard') return 'Security Dashboard';
              if (p.startsWith('/projects-v2')) return 'Projects';
              if (p.includes('/compliance/')) return 'Compliance Assessment';
              if (p.includes('/modeling')) return 'System Modeling';
              if (p.includes('/risk')) return 'Portfolio Status';
              if (p.includes('/threat-intelligence')) return 'Threat Intelligence';
              if (p.includes('/cmmc')) return 'CMMC Assessment';
              if (p.includes('/zero-trust')) return 'Zero Trust Maturity Assessment';
              if (p === '/trust-center') return 'Trust Center';
              if (p === '/reports') return 'Security Reports';
              if (p === '/settings') return 'Settings';
              if (p === '/admin') return 'Admin Console';
              return '';
            })()}
          </Typography>

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={user.role.toUpperCase()}
                size="small"
                color={user.role === 'admin' ? 'error' : 'primary'}
                variant="outlined"
              />

              <IrisNotificationCenter />

              <IconButton
                onClick={handleProfileMenuOpen}
                aria-label="Account menu"
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl) || undefined}
              >
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  {user.first_name[0]}{user.last_name[0]}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem disabled>
                  <Person sx={{ mr: 2 }} />
                  {user.first_name} {user.last_name}
                </MenuItem>
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 2 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{
          width: { sm: drawerOpen ? drawerWidth : 64 },
          flexShrink: { sm: 0 },
          transition: 'all 0.3s',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'transparent !important',
              border: 'none !important',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerOpen ? drawerWidth : 64,
              transition: 'all 0.3s',
              overflowX: 'hidden',
              background: 'transparent !important',
              border: 'none !important',
              borderRight: '1px solid rgba(255,255,255,0.06) !important',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerOpen ? drawerWidth : 64}px)` },
          transition: 'all 0.3s',
          mt: 8,
          ...(isDemo && { userSelect: 'none', WebkitUserSelect: 'none' }),
          ...(devToolsOpen && { filter: 'blur(8px)', transition: 'filter 0.3s' }),
        }}
      >
        {devToolsOpen && (
          <Box
            sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(90deg, rgba(255,82,82,0.15) 0%, rgba(255,152,0,0.1) 100%)',
              border: '1px solid rgba(255,82,82,0.4)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: '#ff5252', fontWeight: 600 }}>
              Developer tools detected. Some content has been obscured for security.
            </Typography>
          </Box>
        )}
        {isDemo && (
          <Box
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              borderRadius: 2,
              background: 'linear-gradient(90deg, rgba(0,217,255,0.12) 0%, rgba(100,255,218,0.08) 100%)',
              border: '1px solid rgba(0,217,255,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64ffda', fontWeight: 500 }}>
              Demo Mode — You are viewing a read-only preview of PRISM Risk Aperture
            </Typography>
            <Chip label="DEMO" size="small" sx={{ bgcolor: 'rgba(0,217,255,0.2)', color: '#00d9ff', fontWeight: 700, letterSpacing: '0.1em' }} />
          </Box>
        )}
        <ApplianceLicenceBanner />
        <Outlet />
      </Box>
      {isDemo && user?.email && <DemoWatermark email={user.email} />}
    </Box>
  );
};

export default Layout;
